import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import * as LucideIcons from 'lucide-react';

// FREE packages (always available)
import * as freeSolid from '@fortawesome/free-solid-svg-icons';
import * as freeRegular from '@fortawesome/free-regular-svg-icons';
import * as freeBrands from '@fortawesome/free-brands-svg-icons';

// PRO packages (will be imported when available)
// import * as proSolid from '@fortawesome/pro-solid-svg-icons';
// import * as proRegular from '@fortawesome/pro-regular-svg-icons';
// import * as proLight from '@fortawesome/pro-light-svg-icons';
// import * as proThin from '@fortawesome/pro-thin-svg-icons';
// import * as proDuotone from '@fortawesome/pro-duotone-svg-icons';
// import * as duotoneRegular from '@fortawesome/duotone-regular-svg-icons';
// import * as duotoneLight from '@fortawesome/duotone-light-svg-icons';
// import * as duotoneThin from '@fortawesome/duotone-thin-svg-icons';
// import * as sharpSolid from '@fortawesome/sharp-solid-svg-icons';
// import * as sharpRegular from '@fortawesome/sharp-regular-svg-icons';
// import * as sharpLight from '@fortawesome/sharp-light-svg-icons';
// import * as sharpThin from '@fortawesome/sharp-thin-svg-icons';
// import * as sharpDuotoneSolid from '@fortawesome/sharp-duotone-solid-svg-icons';
// import * as sharpDuotoneRegular from '@fortawesome/sharp-duotone-regular-svg-icons';
// import * as sharpDuotoneLight from '@fortawesome/sharp-duotone-light-svg-icons';
// import * as sharpDuotoneThin from '@fortawesome/sharp-duotone-thin-svg-icons';

export type IconFamily = 'classic' | 'sharp' | 'brands';
export type IconStyle = 'solid' | 'regular' | 'light' | 'thin' | 'duotone';

/**
 * Get FontAwesome icon from library based on family and style
 *
 * @param iconName - Name of the icon (with or without 'fa' prefix)
 * @param family - Icon family: classic, sharp, or brands
 * @param style - Icon style: solid, regular, light, thin, or duotone
 * @returns IconDefinition or null if not found
 */
export const getIconFromLibrary = (
  iconName: string,
  family: IconFamily = 'classic',
  style: IconStyle = 'solid'
): IconDefinition | null => {

  // Normalize icon name
  let normalizedName = iconName;

  // Remove 'fa' prefix if present
  if (iconName.startsWith('fa')) {
    normalizedName = iconName.charAt(2).toLowerCase() + iconName.slice(3);
  }

  // Convert kebab-case to camelCase (e.g. "hourglass-half" → "hourglassHalf")
  normalizedName = normalizedName.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

  // Convert to camelCase with 'fa' prefix (FontAwesome format)
  const camelCaseName = 'fa' + normalizedName.charAt(0).toUpperCase() + normalizedName.slice(1);

  try {
    // BRANDS family (always free, no style variations)
    if (family === 'brands') {
      const icon = freeBrands[camelCaseName as keyof typeof freeBrands] as IconDefinition;
      if (icon) return icon;
      console.warn(`[IconMapper] Brands icon not found: ${camelCaseName}`);
      return null;
    }

    // SHARP family (Pro only - not available yet)
    if (family === 'sharp') {
      console.warn(`[IconMapper] Sharp icons require Pro subscription. Falling back to classic solid.`);
      // TODO: Uncomment when Pro packages are installed
      // if (style === 'duotone') {
      //   const libs = { solid: sharpDuotoneSolid, regular: sharpDuotoneRegular, light: sharpDuotoneLight, thin: sharpDuotoneThin };
      //   return libs[style][camelCaseName] || null;
      // } else {
      //   const libs = { solid: sharpSolid, regular: sharpRegular, light: sharpLight, thin: sharpThin };
      //   return libs[style][camelCaseName] || null;
      // }

      // Fallback to free solid
      const icon = freeSolid[camelCaseName as keyof typeof freeSolid] as IconDefinition;
      if (icon) return icon;
    }

    // CLASSIC family
    // Try free icons first
    if (style === 'solid') {
      const icon = freeSolid[camelCaseName as keyof typeof freeSolid] as IconDefinition;
      if (icon) return icon;
      // TODO: Try Pro solid when available
      // const proIcon = proSolid[camelCaseName];
      // if (proIcon) return proIcon;
    }

    if (style === 'regular') {
      const icon = freeRegular[camelCaseName as keyof typeof freeRegular] as IconDefinition;
      if (icon) return icon;
      // TODO: Try Pro regular when available
      // const proIcon = proRegular[camelCaseName];
      // if (proIcon) return proIcon;
    }

    // Light, Thin, and Duotone are Pro-only
    if (style === 'light' || style === 'thin' || style === 'duotone') {
      console.warn(`[IconMapper] ${style} style requires Pro subscription. Falling back to free solid.`);
      // TODO: Uncomment when Pro packages are installed
      // if (style === 'light') return proLight[camelCaseName] || null;
      // if (style === 'thin') return proThin[camelCaseName] || null;
      // if (style === 'duotone') return proDuotone[camelCaseName] || null;

      // Fallback to free solid
      const icon = freeSolid[camelCaseName as keyof typeof freeSolid] as IconDefinition;
      if (icon) return icon;
    }

    console.warn(`[IconMapper] Icon not found: ${camelCaseName} (${family}/${style})`);
    return null;

  } catch (error) {
    console.error(`[IconMapper] Error loading icon ${iconName}:`, error);
    return null;
  }
};

/**
 * Universal Icon Renderer Component
 * Supports both Lucide (legacy) and FontAwesome icons
 */
export interface IconRendererProps {
  iconName?: string;
  iconLibrary?: 'lucide' | 'fa';
  iconFamily?: IconFamily;
  iconStyle?: IconStyle;
  className?: string;
  size?: string | number;
  style?: React.CSSProperties;
}

export const IconRenderer: React.FC<IconRendererProps> = ({
  iconName,
  iconLibrary = 'lucide',
  iconFamily = 'classic',
  iconStyle = 'solid',
  className = '',
  size,
  style
}) => {
  if (!iconName) return null;

  // FontAwesome
  if (iconLibrary === 'fa') {
    const icon = getIconFromLibrary(iconName, iconFamily, iconStyle);

    if (!icon) {
      // Fallback to Lucide if FA icon not found
      console.warn(`[IconRenderer] FontAwesome icon not found, falling back to Lucide: ${iconName}`);
      iconLibrary = 'lucide';
    } else {
      return (
        <FontAwesomeIcon
          icon={icon}
          className={className}
          style={style}
          {...(size ? { size: size as any } : {})}
        />
      );
    }
  }

  // Lucide icons (static import)
  const LucideIcon = (LucideIcons as any)[iconName];

  if (!LucideIcon) {
    console.warn(`[IconRenderer] Lucide icon not found: ${iconName}`);
    return null;
  }

  return <LucideIcon className={className} size={size} style={style} />;
};

/**
 * Export icon libraries for IconPicker component
 * Only exports available (free) libraries for now
 */
export const iconLibraries = {
  freeSolid,
  freeRegular,
  freeBrands,
  // Pro libraries will be added here when available
  // proSolid,
  // proRegular,
  // proLight,
  // proThin,
  // proDuotone,
  // duotoneRegular,
  // duotoneLight,
  // duotoneThin,
  // sharpSolid,
  // sharpRegular,
  // sharpLight,
  // sharpThin,
  // sharpDuotoneSolid,
  // sharpDuotoneRegular,
  // sharpDuotoneLight,
  // sharpDuotoneThin
};

/**
 * Get all available icons for a specific family and style
 * Used by IconPicker to display available icons
 */
export const getAvailableIcons = (
  family: IconFamily = 'classic',
  style: IconStyle = 'solid'
): string[] => {
  try {
    let library: any = null;

    if (family === 'brands') {
      library = freeBrands;
    } else if (family === 'classic') {
      if (style === 'solid') library = freeSolid;
      else if (style === 'regular') library = freeRegular;
      // Pro styles will be added when available
    }

    if (!library) {
      console.warn(`[IconMapper] Library not available: ${family}/${style}`);
      return [];
    }

    // Extract icon names (filter out non-icon exports)
    return Object.keys(library).filter(key =>
      key.startsWith('fa') &&
      !key.includes('Definition') &&
      key !== 'fa' &&
      key !== 'fas' &&
      key !== 'far' &&
      key !== 'fab'
    );
  } catch (error) {
    console.error(`[IconMapper] Error getting available icons:`, error);
    return [];
  }
};

/**
 * Mapping from Lucide icon names to FontAwesome equivalents
 * Used for gradual migration from Lucide to FontAwesome
 */
export const lucideToFaMapping: Record<string, string> = {
  'Users': 'faUsers',
  'User': 'faUser',
  'UserCheck': 'faUserCheck',
  'FileText': 'faFileAlt',
  'File': 'faFile',
  'Briefcase': 'faBriefcase',
  'BarChart': 'faChartBar',
  'Calendar': 'faCalendar',
  'CalendarClock': 'faCalendarAlt',
  'Settings': 'faCog',
  'DollarSign': 'faDollarSign',
  'FolderOpen': 'faFolderOpen',
  'Percent': 'faPercent',
  'Handshake': 'faHandshake',
  'CreditCard': 'faCreditCard',
  'Receipt': 'faReceipt',
  'Building2': 'faBuilding',
  'ClipboardList': 'faClipboardList',
  'Send': 'faPaperPlane',
  'Search': 'faSearch',
  'Filter': 'faFilter',
  'Plus': 'faPlus',
  'Edit': 'faEdit',
  'Edit2': 'faEdit',
  'Trash': 'faTrash',
  'Trash2': 'faTrash',
  'X': 'faTimes',
  'Check': 'faCheck',
  'ChevronDown': 'faChevronDown',
  'ChevronRight': 'faChevronRight',
  'ChevronLeft': 'faChevronLeft',
  'ChevronUp': 'faChevronUp',
  'MoreVertical': 'faEllipsisV',
  'MoreHorizontal': 'faEllipsisH',
  'LayoutDashboard': 'faTachometerAlt',
  'LayoutGrid': 'faTh',
  'ListTodo': 'faListAlt',
  'MessageSquare': 'faComment',
  'AlertCircle': 'faExclamationCircle',
  'Package': 'faBox',
  'LifeBuoy': 'faLifeRing',
  'Bell': 'faBell',
  'Mail': 'faEnvelope',
  'Phone': 'faPhone',
  'Download': 'faDownload',
  'Upload': 'faUpload',
  'Eye': 'faEye',
  'EyeOff': 'faEyeSlash',
  'Lock': 'faLock',
  'Unlock': 'faUnlock',
  'Home': 'faHome',
  'Star': 'faStar',
  'Heart': 'faHeart',
  'Share': 'faShare',
  'Copy': 'faCopy',
  'ExternalLink': 'faExternalLinkAlt',
  'Link': 'faLink',
  'Paperclip': 'faPaperclip',
  'Image': 'faImage',
  'Video': 'faVideo',
  'Music': 'faMusic',
  'Mic': 'faMicrophone',
  'Clock': 'faClock',
  'MapPin': 'faMapMarkerAlt',
  'Globe': 'faGlobe',
  'Wifi': 'faWifi',
  'Bluetooth': 'faBluetooth',
  'Battery': 'faBatteryFull',
  'Zap': 'faBolt',
  'Sun': 'faSun',
  'Moon': 'faMoon',
  'Cloud': 'faCloud',
  'Umbrella': 'faUmbrella',
  'TrendingUp': 'faArrowUp',
  'TrendingDown': 'faArrowDown',
  'Activity': 'faChartLine',
  'PieChart': 'faChartPie',
  'Target': 'faBullseye',
  'Award': 'faAward',
  'Gift': 'faGift',
  'ShoppingCart': 'faShoppingCart',
  'ShoppingBag': 'faShoppingBag',
  'Tag': 'faTag',
  'Bookmark': 'faBookmark',
  'Flag': 'faFlag',
  'Printer': 'faPrint',
  'Save': 'faSave',
  'Folder': 'faFolder',
  'Archive': 'faArchive',
  'Inbox': 'faInbox',
  'RefreshCw': 'faSync',
  'RotateCw': 'faRedo',
  'RotateCcw': 'faUndo',
  'Maximize': 'faExpand',
  'Minimize': 'faCompress',
  'ZoomIn': 'faSearchPlus',
  'ZoomOut': 'faSearchMinus',
  'Menu': 'faBars',
  'Grid': 'faTh',
  'List': 'faList',
  'Columns': 'faColumns',
  'Sidebar': 'faBars',
  'Command': 'faTerminal',
  'Terminal': 'faTerminal',
  'Code': 'faCode',
  'Database': 'faDatabase',
  'Server': 'faServer',
  'HardDrive': 'faHdd',
  'Cpu': 'faMicrochip',
  'Power': 'faPowerOff',
  'Settings2': 'faCog',
  'Tool': 'faWrench',
  'Wrench': 'faWrench',
  'Hammer': 'faHammer',
  'Sliders': 'faSlidersH',
  'ToggleLeft': 'faToggleOff',
  'ToggleRight': 'faToggleOn',
};
